// utils/gmailApi.js
// Gmail API helper functions

/**
 * Vytvoří draft v Gmailu s HTML obsahem
 * @param {string} accessToken - Gmail API access token
 * @param {string} subject - Předmět emailu
 * @param {string} htmlContent - HTML obsah emailu
 * @param {string} to - Příjemce (volitelné)
 * @returns {Promise<object>} Draft response
 */
export async function createGmailDraft(accessToken, subject, htmlContent, to = '') {
  // Enkóduj subject podle RFC 2047 (MIME encoded-word)
  const encodedSubject = `=?UTF-8?B?${btoa(unescape(encodeURIComponent(subject)))}?=`;

  // Vytvoř RFC 2822 formatted email
  const email = [
    to ? `To: ${to}` : '',
    `Subject: ${encodedSubject}`,
    'Content-Type: text/html; charset=utf-8',
    'MIME-Version: 1.0',
    '',
    htmlContent
  ].filter(line => line !== '').join('\r\n');

  // Enkóduj do base64url pomocí FileReader a Blob
  // Tento přístup spolehlivě zpracuje UTF-8 ve všech prohlížečích
  const base64Email = await new Promise((resolve, reject) => {
    const blob = new Blob([email], { type: 'text/plain; charset=utf-8' });
    const reader = new FileReader();

    reader.onload = () => {
      // FileReader vrátí data URL ve formátu "data:text/plain;base64,..."
      const base64 = reader.result.split(',')[1];
      // Převeď na base64url (bez padding)
      const base64url = base64
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      resolve(base64url);
    };

    reader.onerror = () => reject(new Error('Failed to encode email'));
    reader.readAsDataURL(blob);
  });

  // Vytvoř draft přes Gmail API
  const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/drafts', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      message: {
        raw: base64Email
      }
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Gmail API error: ${error.error?.message || 'Unknown error'}`);
  }

  return response.json();
}

/**
 * Zkontroluje zda má uživatel platný Gmail access token
 * @returns {boolean}
 */
export function hasGmailAuth() {
  try {
    const tokens = localStorage.getItem('gmail_tokens');
    if (!tokens) return false;

    const parsed = JSON.parse(tokens);
    return !!(parsed.access_token);
  } catch {
    return false;
  }
}

/**
 * Získá Gmail access token z localStorage
 * @returns {string|null}
 */
export function getGmailAccessToken() {
  try {
    const tokens = localStorage.getItem('gmail_tokens');
    if (!tokens) return null;

    const parsed = JSON.parse(tokens);
    return parsed.access_token || null;
  } catch {
    return null;
  }
}

/**
 * Otevře Gmail OAuth2 flow v novém okně
 * @returns {Promise<object>} Tokens po úspěšné autorizaci
 */
export function requestGmailAuth() {
  return new Promise((resolve, reject) => {
    // Otevři OAuth2 flow v popup
    const authWindow = window.open(
      '/api/auth/gmail-authorize',
      'Gmail Authorization',
      'width=500,height=600'
    );

    if (!authWindow) {
      reject(new Error('Popup bylo zablokováno. Povolte vyskakovací okna.'));
      return;
    }

    // Čekej na postMessage s tokeny
    const messageHandler = async (event) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'gmail_auth_success') {
        window.removeEventListener('message', messageHandler);

        // Redeem token key from server (tokens are stored in Redis, not inlined in HTML)
        if (event.data.tokenKey) {
          try {
            const resp = await fetch('/api/auth/gmail-redeem-tokens', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ tokenKey: event.data.tokenKey }),
            });
            if (resp.ok) {
              const data = await resp.json();
              localStorage.setItem('gmail_tokens', JSON.stringify(data.tokens));
              resolve(data.tokens);
            } else {
              reject(new Error('Failed to redeem Gmail tokens'));
            }
          } catch (err) {
            reject(err);
          }
        } else if (event.data.tokens) {
          // Legacy fallback
          localStorage.setItem('gmail_tokens', JSON.stringify(event.data.tokens));
          resolve(event.data.tokens);
        }
      }
    };

    window.addEventListener('message', messageHandler);

    // Timeout po 5 minutách
    setTimeout(() => {
      window.removeEventListener('message', messageHandler);
      reject(new Error('Authorization timeout'));
    }, 5 * 60 * 1000);
  });
}

/**
 * Odešle email přes Gmail API
 * @param {string} accessToken - Gmail API access token
 * @param {string} to - Příjemce emailu
 * @param {string} subject - Předmět emailu
 * @param {string} body - Text emailu (plain text)
 * @returns {Promise<object>} Send response
 */
export async function sendGmailEmail(accessToken, to, subject, body) {
  // Enkóduj subject podle RFC 2047 (MIME encoded-word)
  const encodedSubject = `=?UTF-8?B?${btoa(unescape(encodeURIComponent(subject)))}?=`;

  // Vytvoř RFC 2822 formatted email
  const email = [
    `To: ${to}`,
    `Subject: ${encodedSubject}`,
    'Content-Type: text/plain; charset=utf-8',
    'MIME-Version: 1.0',
    '',
    body
  ].join('\r\n');

  // Enkóduj do base64url
  const base64Email = await new Promise((resolve, reject) => {
    const blob = new Blob([email], { type: 'text/plain; charset=utf-8' });
    const reader = new FileReader();

    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      const base64url = base64
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
      resolve(base64url);
    };

    reader.onerror = () => reject(new Error('Failed to encode email'));
    reader.readAsDataURL(blob);
  });

  // Odešli email přes Gmail API
  const response = await fetch('https://gmail.googleapis.com/gmail/v1/users/me/messages/send', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      raw: base64Email
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(`Gmail API error: ${error.error?.message || 'Unknown error'}`);
  }

  return response.json();
}
